import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  UsersIcon,
  ArrowPathIcon,
  QrCodeIcon,
  BuildingOffice2Icon,
  DocumentChartBarIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { DateDisplay } from '../../components/Forms';
import AdminDashboard from './AdminDashboard';
import {
  canSeeNavByPermissions,
  PERMISSION_MODULES,
} from '../../utils/userPermissions';
import { BoardingBusSummary, Trip } from '../../types';
import { getLocalToday, toGregorianDateOnly } from '../../utils/dateDisplay';
import { getTripHailDepartureGregorian } from '../../utils/bookingDeparture';
import { formatHijriLabel } from '../../components/Forms/hijriDate';

interface DashboardStats {
  today: { bookings: number; revenue: number };
  month: { bookings: number; revenue: number };
  pending: { bookings: number };
  active: { trips: number };
}

type StatTone = 'primary' | 'green' | 'amber' | 'gray';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-sm font-semibold text-gray-500">{children}</h2>
);

const ROLE_LABEL: Record<string, string> = {
  admin: 'مدير النظام',
  operations: 'عمليات / صعود',
  hotel_rep: 'مندوب فندق',
  accounting: 'محاسبة',
  super_admin: 'مدير أعلى',
};

const MODULE_LABEL: Record<string, string> = Object.fromEntries(
  PERMISSION_MODULES.map((m) => [m.module, m.label])
);

const getLocalTomorrow = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getTripRouteLabel = (trip: Trip): string => {
  if (trip.routes?.length) {
    const active = trip.routes.find((r) => r.isActive) || trip.routes[0];
    if (active?.from && active?.to) return `${active.from} → ${active.to}`;
  }
  if (trip.route?.from && trip.route?.to) {
    return `${trip.route.from} → ${trip.route.to}`;
  }
  return '';
};

const dateHeadingLabel = (dateKey: string, today: string, tomorrow: string) => {
  if (dateKey === today) return 'اليوم';
  if (dateKey === tomorrow) return 'غداً';
  if (dateKey && dateKey < today) return 'سابقة';
  const hijri = dateKey ? formatHijriLabel(dateKey) : '';
  return hijri || dateKey || 'بدون تاريخ';
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    today: { bookings: 0, revenue: 0 },
    month: { bookings: 0, revenue: 0 },
    pending: { bookings: 0 },
    active: { trips: 0 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const localToday = getLocalToday();
  const localTomorrow = getLocalTomorrow();

  const currentVendor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('currentVendor') || '{}');
    } catch {
      return {};
    }
  }, []);

  const canBookings = canSeeNavByPermissions(user, 'bookings');
  const canBoarding = canSeeNavByPermissions(user, 'boarding');
  const canHousing = canSeeNavByPermissions(user, 'housing');
  const canCash = canSeeNavByPermissions(user, 'cash-closing');
  const canReports = canSeeNavByPermissions(user, 'reports');
  const canTrips = canSeeNavByPermissions(user, 'trips');
  const canUsers = canSeeNavByPermissions(user, 'users');
  const canSettings = canSeeNavByPermissions(user, 'settings');

  const canSeeBusinessStats =
    canBookings || canReports || canCash || user?.role === 'admin';

  const seesAllTrips = ['admin', 'super_admin'].includes(user?.role || '');
  const base = '/vendor';

  const { data: myTripsData, isLoading: myTripsLoading } = useQuery({
    queryKey: ['dashboard-my-boarding-trips', user?._id],
    queryFn: () =>
      apiService.getTrips(1, 20, seesAllTrips ? {} : { boardingMine: true }),
    enabled: !!canBoarding && !!currentVendor.vendorId,
  });

  const myTrips = useMemo(() => {
    const list = myTripsData?.data || [];
    return list.filter((t: Trip) =>
      ['published', 'full', 'ongoing', 'draft'].includes(t.status || '')
    );
  }, [myTripsData]);

  const busQueries = useQueries({
    queries: myTrips.map((t) => ({
      queryKey: ['dashboard-boarding-buses', t._id, user?._id],
      queryFn: () => apiService.getBoardingBuses(t._id),
      enabled: !!canBoarding && !!t._id,
    })),
  });

  const busesLoading = busQueries.some((q) => q.isLoading);
  const busesDataKey = busQueries
    .map((q) => JSON.stringify(q.data?.buses || []))
    .join('|');

  const boardingOverview = useMemo(() => {
    let assignedBuses = 0;
    const perTrip: { trip: Trip; buses: BoardingBusSummary[]; dateKey: string }[] =
      [];

    myTrips.forEach((trip, i) => {
      const buses = busQueries[i]?.data?.buses || [];
      assignedBuses += buses.filter((b) => b.busId).length;
      const dateKey =
        getTripHailDepartureGregorian(trip) ||
        toGregorianDateOnly(trip.date) ||
        '';
      perTrip.push({ trip, buses, dateKey });
    });

    const today = getLocalToday();
    const tomorrow = getLocalTomorrow();

    const upcomingTrips = perTrip.filter(
      ({ trip, dateKey }) =>
        trip.status === 'ongoing' || !dateKey || dateKey >= today
    );
    const pastTrips = perTrip.filter(
      ({ trip, dateKey }) =>
        trip.status !== 'ongoing' && !!dateKey && dateKey < today
    );

    const groupByDate = (
      items: typeof perTrip,
      opts?: { past?: boolean }
    ) => {
      const map = new Map<
        string,
        { dateKey: string; label: string; items: typeof perTrip }
      >();
      items.forEach((item) => {
        const key = item.dateKey || 'unknown';
        if (!map.has(key)) {
          map.set(key, {
            dateKey: key,
            label:
              key === 'unknown'
                ? 'بدون تاريخ'
                : dateHeadingLabel(key, today, tomorrow),
            items: [],
          });
        }
        map.get(key)!.items.push(item);
      });
      const groups = Array.from(map.values());
      groups.sort((a, b) => {
        if (a.dateKey === 'unknown') return 1;
        if (b.dateKey === 'unknown') return -1;
        return opts?.past
          ? b.dateKey.localeCompare(a.dateKey)
          : a.dateKey.localeCompare(b.dateKey);
      });
      return groups;
    };

    const todayCount = upcomingTrips.filter((t) => t.dateKey === today).length;

    return {
      tripCount: myTrips.length,
      assignedBuses,
      todayCount,
      upcomingGroups: groupByDate(upcomingTrips),
      pastGroups: groupByDate(pastTrips, { past: true }),
      perTrip,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTrips, busesDataKey]);

  const { data: housingIncomplete, isLoading: housingIncompleteLoading } =
    useQuery({
      queryKey: ['dashboard-housing-incomplete', user?._id],
      queryFn: () => apiService.getHousingBookings(1, 1, { complete: 'false' }),
      enabled: !!canHousing && !!currentVendor.vendorId,
    });

  const { data: housingComplete, isLoading: housingCompleteLoading } = useQuery({
    queryKey: ['dashboard-housing-complete', user?._id],
    queryFn: () => apiService.getHousingBookings(1, 1, { complete: 'true' }),
    enabled: !!canHousing && !!currentVendor.vendorId,
  });

  const housingLoading = housingIncompleteLoading || housingCompleteLoading;
  const housingPendingCount = housingIncomplete?.pagination?.total || 0;
  const housingDoneCount = housingComplete?.pagination?.total || 0;
  const housingTotalCount = housingPendingCount + housingDoneCount;

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (user?.role === 'super_admin' || !currentVendor.vendorId) {
        setStats({
          today: { bookings: 0, revenue: 0 },
          month: { bookings: 0, revenue: 0 },
          pending: { bookings: 0 },
          active: { trips: 0 },
        });
        return;
      }

      if (!canSeeBusinessStats) {
        setStats({
          today: { bookings: 0, revenue: 0 },
          month: { bookings: 0, revenue: 0 },
          pending: { bookings: 0 },
          active: { trips: 0 },
        });
        return;
      }

      const dashboardStats = await apiService.getDashboardStats();
      setStats(dashboardStats);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('فشل في تحميل بيانات لوحة التحكم');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: unknown) => {
    const value = typeof amount === 'number' ? amount : Number(amount);
    const safe = Number.isFinite(value) ? value : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
    })
      .format(safe)
      .replace(/SAR\s*/g, 'ر.س ');
  };

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('en-US').format(num);

  const TONE: Record<StatTone, string> = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    gray: 'bg-gray-100 text-gray-500',
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ElementType;
    tone?: StatTone;
    isLoading?: boolean;
  }> = ({ title, value, icon: Icon, tone = 'primary', isLoading: loading }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-gray-500">{title}</p>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TONE[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">
        {loading ? <div className="h-8 w-24 animate-pulse rounded bg-gray-100" /> : value}
      </div>
    </div>
  );

  const quickLinks = useMemo(() => {
    const links: Array<{
      key: string;
      title: string;
      desc: string;
      href: string;
      icon: React.ElementType;
    }> = [];

    if (canHousing) {
      links.push({
        key: 'housing-scan',
        title: 'تصوير QR',
        desc: 'مسح رمز الزائر وعرض السكن',
        href: `${base}/housing?scan=1`,
        icon: QrCodeIcon,
      });
      links.push({
        key: 'housing',
        title: 'التسكين',
        desc: 'تعيين الغرف وموقع الفندق',
        href: `${base}/housing`,
        icon: BuildingOffice2Icon,
      });
    }
    if (canBoarding) {
      links.push({
        key: 'boarding',
        title: 'صعود الباص',
        desc: 'مسح QR وتسجيل الحضور',
        href: `${base}/boarding`,
        icon: QrCodeIcon,
      });
    }
    if (canBookings) {
      links.push({
        key: 'bookings',
        title: 'الحجوزات',
        desc: 'إنشاء ومتابعة الحجوزات',
        href: `${base}/bookings`,
        icon: CalendarIcon,
      });
    }
    if (canTrips) {
      links.push({
        key: 'trips',
        title: 'الرحلات',
        desc: 'إدارة الرحلات والباصات',
        href: `${base}/trips`,
        icon: TruckIcon,
      });
    }
    if (canCash) {
      links.push({
        key: 'cash',
        title: 'إغلاق الصندوق',
        desc: 'المحاسبة اليومية',
        href: `${base}/cash-closing`,
        icon: BanknotesIcon,
      });
    }
    if (canReports) {
      links.push({
        key: 'reports',
        title: 'التقارير',
        desc: 'تقارير الأداء والإيرادات',
        href: `${base}/reports`,
        icon: DocumentChartBarIcon,
      });
    }
    if (canUsers) {
      links.push({
        key: 'users',
        title: 'المستخدمين',
        desc: 'إدارة حسابات الموظفين',
        href: `${base}/users`,
        icon: UsersIcon,
      });
    }
    if (canSettings) {
      links.push({
        key: 'settings',
        title: 'الإعدادات',
        desc: 'إعدادات المكتب',
        href: `${base}/settings`,
        icon: Cog6ToothIcon,
      });
    }

    return links;
  }, [
    canHousing,
    canBoarding,
    canBookings,
    canTrips,
    canCash,
    canReports,
    canUsers,
    canSettings,
  ]);

  if (user?.role === 'super_admin' && !currentVendor.vendorId) {
    return <AdminDashboard />;
  }

  const activePerms = (user?.permissions || [])
    .filter((p) => (p.actions || []).length > 0)
    .map((p) => p.module);

  return (
    <div className="space-y-8 rtl">
      {/* Welcome */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">أهلاً، {user?.fullName}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {ROLE_LABEL[user?.role || ''] || user?.role}
            {user?.branch?.name && ` · ${user.branch.name}`}
            {currentVendor.vendorName && ` · ${currentVendor.vendorName}`}
          </p>
          {activePerms.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {activePerms.map((m) => (
                <span key={m} className="badge bg-gray-100 text-gray-600">
                  {MODULE_LABEL[m] || m}
                </span>
              ))}
            </div>
          )}
        </div>
        {(canSeeBusinessStats || canHousing || canBoarding) && (
          <button
            onClick={loadDashboardData}
            disabled={isLoading}
            className="btn-secondary py-2"
            type="button"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        )}
      </div>

      {error && canSeeBusinessStats && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <div>
            <h3 className="text-sm font-medium text-red-800">خطأ في تحميل البيانات</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
          <button
            type="button"
            className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-50"
            onClick={loadDashboardData}
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {/* Business stats */}
      {canSeeBusinessStats && (
        <section className="space-y-4">
          <SectionTitle>نظرة عامة</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="حجوزات اليوم"
              value={formatNumber(stats.today.bookings)}
              icon={CalendarIcon}
              isLoading={isLoading}
            />
            <StatCard
              title="إيرادات اليوم"
              value={formatCurrency(stats.today.revenue)}
              icon={CurrencyDollarIcon}
              tone="green"
              isLoading={isLoading}
            />
            <StatCard
              title="حجوزات الشهر"
              value={formatNumber(stats.month.bookings)}
              icon={UsersIcon}
              isLoading={isLoading}
            />
            <StatCard
              title="إيرادات الشهر"
              value={formatCurrency(stats.month.revenue)}
              icon={CurrencyDollarIcon}
              tone="green"
              isLoading={isLoading}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              title="الحجوزات المعلقة"
              value={formatNumber(stats.pending.bookings)}
              icon={CalendarIcon}
              tone="amber"
              isLoading={isLoading}
            />
            <StatCard
              title="الرحلات النشطة"
              value={formatNumber(stats.active.trips)}
              icon={TruckIcon}
              tone="gray"
              isLoading={isLoading}
            />
            <StatCard
              title="إجمالي الحجوزات"
              value={formatNumber(stats.today.bookings + stats.month.bookings)}
              icon={UsersIcon}
              tone="gray"
              isLoading={isLoading}
            />
          </div>
        </section>
      )}

      {/* Quick links based on permissions */}
      {quickLinks.length > 0 && (
        <section className="space-y-4">
          <SectionTitle>وصول سريع</SectionTitle>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.key}
                  to={link.href}
                  className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 transition-colors group-hover:bg-primary-100 group-hover:text-primary-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{link.title}</p>
                    <p className="truncate text-xs text-gray-500">{link.desc}</p>
                  </div>
                  <ChevronLeftIcon className="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-primary-500" />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Housing dashboard */}
      {canHousing && (
        <section className="space-y-4">
          <SectionTitle>التسكين</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              title="إجمالي حجوزات السكن"
              value={formatNumber(housingTotalCount)}
              icon={BuildingOffice2Icon}
              isLoading={housingLoading}
            />
            <StatCard
              title="تسكين ناقص"
              value={formatNumber(housingPendingCount)}
              icon={BuildingOffice2Icon}
              tone="amber"
              isLoading={housingLoading}
            />
            <StatCard
              title="تسكين مكتمل"
              value={formatNumber(housingDoneCount)}
              icon={BuildingOffice2Icon}
              tone="green"
              isLoading={housingLoading}
            />
          </div>
          <div className="card flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">مهام التسكين</h3>
              <p className="mt-1 text-sm text-gray-500">
                {housingPendingCount > 0
                  ? `يوجد ${housingPendingCount} حجز بحاجة لتعيين غرف أو موقع.`
                  : 'لا توجد حجوزات ناقصة حالياً.'}
              </p>
            </div>
            <Link to={`${base}/housing`} className="btn-primary py-2 text-sm">
              فتح التسكين
            </Link>
          </div>
        </section>
      )}

      {/* Boarding staff — upcoming trips by date */}
      {canBoarding && (
        <section className="space-y-4">
          <SectionTitle>الصعود</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              title="رحلات اليوم"
              value={formatNumber(boardingOverview.todayCount)}
              icon={CalendarIcon}
              tone="green"
              isLoading={myTripsLoading}
            />
            <StatCard
              title="رحلات معيّنة لي"
              value={formatNumber(boardingOverview.tripCount)}
              icon={TruckIcon}
              isLoading={myTripsLoading}
            />
            <StatCard
              title="باصات معيّنة لي"
              value={formatNumber(boardingOverview.assignedBuses)}
              icon={TruckIcon}
              tone="gray"
              isLoading={myTripsLoading || busesLoading}
            />
          </div>

          <div className="card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                <QrCodeIcon className="h-5 w-5 text-gray-400" />
                {seesAllTrips ? 'الرحلات القادمة للصعود' : 'رحلاتك القادمة'}
              </h3>
              <Link to={`${base}/boarding`} className="text-sm font-medium text-primary-700 hover:text-primary-800">
                فتح صفحة الصعود
              </Link>
            </div>

            {myTripsLoading || busesLoading ? (
              <p className="text-sm text-gray-500">جاري التحميل...</p>
            ) : boardingOverview.upcomingGroups.length === 0 &&
              boardingOverview.pastGroups.length === 0 ? (
              <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                {seesAllTrips
                  ? 'لا توجد رحلات نشطة حالياً.'
                  : 'لا توجد رحلات معيّنة لك. تواصل مع المدير لتعيين باص من نموذج الرحلة أو الحجز اليدوي.'}
              </p>
            ) : boardingOverview.upcomingGroups.length === 0 ? (
              <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                لا توجد رحلات قادمة حالياً.
              </p>
            ) : (
              <div className="space-y-5">
                {boardingOverview.upcomingGroups.map((group) => (
                  <div key={group.dateKey || 'unknown'}>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span
                        className={`badge ${
                          group.dateKey === localToday
                            ? 'bg-green-50 text-green-700'
                            : group.dateKey === localTomorrow
                              ? 'bg-primary-50 text-primary-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {group.label}
                      </span>
                      {group.dateKey && group.dateKey !== 'unknown' && (
                        <span className="text-xs text-gray-500">
                          <DateDisplay value={group.dateKey} className="inline-flex" />
                        </span>
                      )}
                      <span className="text-xs text-gray-400">({group.items.length} رحلة)</span>
                    </div>
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
                      {group.items.map(({ trip: t, buses }) => {
                        const routeLabel = getTripRouteLabel(t);
                        const assigned = buses.filter((b) => b.busId);
                        return (
                          <li
                            key={t._id}
                            className="flex flex-col gap-3 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div className="min-w-0">
                              <p className="flex flex-wrap items-center gap-2 font-medium text-gray-900">
                                {t.tripNumber}
                                {t.status === 'ongoing' && (
                                  <span className="badge bg-amber-50 text-amber-700">جارية</span>
                                )}
                              </p>
                              {routeLabel && <p className="mt-0.5 text-sm text-gray-600">{routeLabel}</p>}
                              <p className="mt-0.5 text-xs text-gray-500">
                                {assigned.length > 0
                                  ? `باص: ${assigned.map((b) => b.busNumber || 'باص').join('، ')}`
                                  : 'بدون باص ظاهر'}
                              </p>
                            </div>
                            <Link
                              to={`${base}/boarding?tripId=${t._id}`}
                              className="btn-primary shrink-0 px-4 py-1.5 text-sm"
                            >
                              صعود
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {boardingOverview.pastGroups.length > 0 && (
              <details className="mt-5 border-t border-gray-100 pt-4">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                  رحلات سابقة ({boardingOverview.pastGroups.reduce((n, g) => n + g.items.length, 0)})
                </summary>
                <div className="mt-3 space-y-4">
                  {boardingOverview.pastGroups.map((group) => (
                    <div key={`past-${group.dateKey}`}>
                      <p className="mb-1 text-xs font-medium text-gray-500">
                        {group.label}
                        {group.dateKey !== 'unknown' && (
                          <>
                            {' — '}
                            <DateDisplay value={group.dateKey} className="inline-flex" />
                          </>
                        )}
                      </p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        {group.items.map(({ trip: t }) => (
                          <li key={t._id} className="flex justify-between gap-2">
                            <span>{t.tripNumber}</span>
                            <Link
                              to={`${base}/boarding?tripId=${t._id}`}
                              className="text-xs text-primary-700 hover:underline"
                            >
                              فتح
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </section>
      )}

      {quickLinks.length === 0 && (
        <div className="card text-sm text-gray-600">
          لا توجد صلاحيات مفعّلة لهذا الحساب بعد. اطلبي من المدير تفعيل الوحدات من إدارة
          المستخدمين.
        </div>
      )}
    </div>
  );
};

export default Dashboard;
